import React, { memo, useContext, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { checkEmailValidity, isValidObject, isValidString } from '../../../../catalog/Validity';
import { context } from '../../../../globalState/GlobalState';
import './css/TicketsComponent1.css';
import { showFeesFromCents, showPriceFromCents, showProfitFromCents, showPlatformFees } from '../../../../catalog/Price';
import Button1 from '../../buttons/Button1';
import { checkoutAPI } from '../../../apiCalls/StoreAPICalls';
import { getTicketURL } from '../../../../catalog/Social';
import { TicketEventType } from '../../../../catalog/enums/TicketEventType';
import TicketCustomerInfoPopup from './TicketsCustomerInfoPopup';
import { addTicketToCart, checkTicketsValidity, getSuccessURL } from '../../../../catalog/Store';
import ShippingReturnPolicyComponent from '../../storeComponents/shippingReturnPolicyComponent/ShippingReturnPolicyComponent';
import { randomNumber } from '../../../../catalog/Others';
import TicketsTicketComponent1 from './TicketsTicketComponent1';
import Spinner1 from '../../spinners/spinner1/Spinner1';

const initialState = {
    quantity: {},
    customerInfoPopup: false,
    customerName: '',
    customerEmail: '',
    checkoutLoadingSpinner: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'reset':
            return { ...initialState };
        case 'incrementQuantity':
            if (isValidObject(state.quantity[action.id])) {
                return { ...state, quantity: { ...state.quantity, [action.id]: state.quantity[action.id] + 1 } };
            }
            return { ...state, quantity: { ...state.quantity, [action.id]: 1 } };
        case 'decrementQuantity':
            if (isValidObject(state.quantity[action.id]) && state.quantity[action.id] > 0) {
                return { ...state, quantity: { ...state.quantity, [action.id]: state.quantity[action.id] - 1 } };
            }
            return { ...state, quantity: { ...state.quantity, [action.id]: 0 } };
        case 'openCustomerInfoPopup':
            return { ...state, customerInfoPopup: true };
        case 'closeCustomerInfoPopup':
            return { ...state, customerInfoPopup: false };
        case 'setCustomerName':
            return { ...state, customerName: action.name };
        case 'setCustomerEmail':
            return { ...state, customerEmail: action.email };
        case 'setLoadingSpinner':
            return { ...state, checkoutLoadingSpinner: action.loadingSpinner };
        default:
            return initialState;
    }
}

const TicketComponent1 = (props) => {
    const { ticket, loadingSpinner } = props;
    const [state, setState] = useReducer(reducer, initialState);
    const { globalState, setGlobalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();

    const getTotalPricePerTicket = (price) => {
        if (isValidObject(state.quantity[price.id])) {
            return price.price * state.quantity[price.id];
        }
        return 0;
    };

    const getTotalPrice = () => {
        let total = 0;
        ticket.prices.forEach((price) => {
            total += getTotalPricePerTicket(price);
        });
        return total;
    };

    const getButtonDisabled = () => {
        let disabled = true;
        ticket.prices.forEach((price) => {
            if (isValidObject(state.quantity[price.id]) && state.quantity[price.id] > 0) {
                disabled = false;
            }
        });
        return disabled;
    };

    const addToCart = () => {
        if (!checkTicketsValidity(globalState)) {
            enqueueSnackbar('This ticket is not available for purchase at the moment', { variant: 'error' });
            return;
        }

        const cartItems = [];

        ticket.prices.forEach((price) => {
            if (isValidObject(state.quantity[price.id]) && state.quantity[price.id] > 0) {
                cartItems.push({ cartItemType: 'TICKET', cartItemId: price.id, ticketId: ticket.id, stripePriceId: price.stripePriceId, quantity: state.quantity[price.id], shippable: false, shippingPrice: 0, shippingWeight: 0 });
            }
        });

        addTicketToCart(cartItems, setGlobalState, enqueueSnackbar);
        setState({ type: 'reset' });
    };

    const checkout = async () => {
        if (!checkTicketsValidity(globalState)) {
            enqueueSnackbar('This ticket is not available for purchase at the moment', { variant: 'error' });
            return;
        }

        setState({ type: 'setLoadingSpinner', loadingSpinner: true });
        const stripePrices = [];
        let totalPrice = 0;
        ticket.prices.forEach((price) => {
            if (isValidObject(state.quantity[price.id]) && state.quantity[price.id] > 0) {
                stripePrices.push({ id: price.stripePriceId, quantity: state.quantity[price.id], cartItemId: randomNumber(), shippable: false, shippingPrice: 0, shippingWeight: 0 });
                totalPrice += price.price;
            }
        });

        if (totalPrice === 0) {
            if (state.customerInfoPopup) {
                if (!isValidString(state.customerName)) {
                    enqueueSnackbar('Please enter valid customer name', { variant: 'error' });
                    return;
                }
                if (!checkEmailValidity(state.customerEmail, () => {}, enqueueSnackbar)) {
                    enqueueSnackbar('Please enter valid customer email', { variant: 'error' });
                    return;
                }
            } else {
                setState({ type: 'openCustomerInfoPopup' });
                return;
            }
        }

        const cancelUrl = `${getTicketURL(globalState, ticket.eventId)}`;
        await checkoutAPI(globalState.stripeAccountId, stripePrices, state.customerName, state.customerEmail, {}, false, 0, 0, getSuccessURL(globalState), cancelUrl, enqueueSnackbar);

        setState({ type: 'setLoadingSpinner', loadingSpinner: false });
        setState({ type: 'reset' });
    };

    return (
        <>
            {loadingSpinner ? <Spinner1 color={globalState.websiteStyles.colors.primaryColor} /> : (
                <>
                    <div className="tickets-component1-container-container x-center m-b-50px" id="ticketTitle">
                        <div className="tickets-component1-container box-shadow-black bg-white curve-border-40px x-center p-t-100px p-b-100px p-l-100px p-r-100px m-t-50px">
                            <h1 className="tickets-component1-title alegreya-font-family font-weight-800 text-left-align text-uppercase m-t-0 m-b-0 m-l-20px m-r-20px">
                                {ticket.eventType === TicketEventType.TICKET_EVENT ? ticket.title : 'TICKETS'}
                            </h1>
                            <TicketsTicketComponent1 ticket={ticket} quantity={state.quantity} setState={setState} />
                            {showPlatformFees ? (
                                <>
                                    <div className="tickets-component1-subprice-footer-container m-l-20px m-r-20px m-t-30px">
                                        <p className="tickets-component1-subprice-footer-total-label alegreya-font-family font-weight-800 font-size-2xl text-uppercase text-right-align m-t-0 m-b-0">Subtotal:</p>
                                        <p className="tickets-component1-subprice-footer-total text-right-align m-t-0 m-b-0">{showProfitFromCents(getTotalPrice())}</p>
                                    </div>
                                    <div className="tickets-component1-subprice-footer-container m-l-20px m-r-20px">
                                        <p className="tickets-component1-subprice-footer-total-label alegreya-font-family font-weight-800 font-size-2xl text-uppercase text-right-align m-t-0 m-b-0">Platform Fee:</p>
                                        <p className="tickets-component1-subprice-footer-total text-right-align m-t-0 m-b-0">{showFeesFromCents(getTotalPrice())}</p>
                                    </div>
                                </>
                            ) : null}
                            <div className="tickets-component1-price-footer-container m-l-20px m-r-20px">
                                <p className="tickets-component1-price-footer-total-label alegreya-font-family font-weight-800 font-size-3xl text-uppercase text-center-align m-t-0 m-b-0">Total</p>
                                <p className="tickets-component1-price-footer-total text-right-align m-t-0 m-b-0">{showPriceFromCents(getTotalPrice())}</p>
                            </div>
                            <div className="tickets-component1-buttons">
                                <Button1
                                    onClick={addToCart}
                                    title="Add to Cart"
                                    disabled={getButtonDisabled()}
                                    transparent
                                    backgroundColor={globalState.websiteStyles.colors.primaryColor}
                                    fontColor={isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor}` : 'white'}
                                    fontFamily="HelveticaNeue-CondensedBold, Roboto Condensed, Helvetica Neue, Helvetica,Arial, sans-serif"
                                    borderRadius="5px"
                                    width="250px"
                                    height="44px"
                                />
                                <Button1
                                    onClick={checkout}
                                    title="Buy Now"
                                    disabled={getButtonDisabled()}
                                    backgroundColor={globalState.websiteStyles.colors.primaryColor}
                                    fontColor={isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor}` : 'white'}
                                    fontFamily="HelveticaNeue-CondensedBold, Roboto Condensed, Helvetica Neue, Helvetica,Arial, sans-serif"
                                    borderRadius="5px"
                                    width="250px"
                                    height="44px"
                                    loadingSpinner={state.checkoutLoadingSpinner}
                                />
                            </div>
                            <div className="m-l-20px m-r-20px m-t-50px">
                                <ShippingReturnPolicyComponent showShipping={false} />
                            </div>
                            {isValidObject(globalState.logo) && isValidString(globalState.logo.logo2) ? (
                                <img src={globalState.logo.logo2} className="tickets-component1-icon m-t-50px m-b-0" alt={isValidString(globalState.teamName) ? globalState.teamName : 'Team Logo'} />
                            ) : null}
                            <TicketCustomerInfoPopup
                                open={state.customerInfoPopup}
                                onCancel={() => {
                                    setState({ type: 'closeCustomerInfoPopup' });
                                    setState({ type: 'setLoadingSpinner', loadingSpinner: false });
                                }}
                                name={state.customerName}
                                email={state.customerEmail}
                                onNameUpdate={(name) => { setState({ type: 'setCustomerName', name }); }}
                                onEmailUpdate={(email) => { setState({ type: 'setCustomerEmail', email }); }}
                                checkout={() => {
                                    checkout();
                                    setState({ type: 'closeCustomerInfoPopup' });
                                }}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

TicketComponent1.propTypes = {
    ticket: PropTypes.object.isRequired,
    loadingSpinner: PropTypes.bool.isRequired,
};

export default memo(TicketComponent1);
